import { useEffect } from 'react';
import SeoJsonLd from '../../components/feature/SeoJsonLd';

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Service - Caption King';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Read Caption King\'s terms of service to understand the rules and guidelines for using our AI Instagram caption generator service.');
    }
  }, []);

  const seoData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms of Service',
    description: 'Terms of service for Caption King - AI Instagram Caption Generator',
    url: 'https://tryaikit.com/terms',
    publisher: {
      '@type': 'Organization',
      name: 'Caption King',
    },
  };

  return (
    <>
      <SeoJsonLd data={seoData} />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white">
        {/* Navigation */}
        <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-2">
                <img src="https://public.readdy.ai/ai/img_res/15ec66b2-609e-40f0-bdad-7a5a43050de3.png" alt="Caption King" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
                <span className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Caption King
                </span>
              </a>
              <a 
                href="/"
                className="px-3 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-base rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap"
              >
                Back to Home
              </a>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3 sm:mb-4">
            Terms of Service
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mb-8 sm:mb-12">Last updated: January 15, 2025</p>
          
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-8 space-y-6 sm:space-y-8">
            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">1. Agreement to Terms</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                By accessing or using Caption King ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service. These Terms apply to all visitors, users, and others who access or use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">2. Description of Service</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Caption King is an AI-powered service that generates Instagram captions from uploaded photos. The Service analyzes your images and creates multiple caption styles including funny, deep, short, story, and question formats. We offer various pricing plans including free trials, subscription-based access, and credit-based purchases.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">3. User Accounts</h2>
              <div className="space-y-2 sm:space-y-3">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  To use certain features of the Service, you must create an account. You agree to:
                </p>
                <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and promptly update your account information</li>
                  <li>Maintain the security of your password and account</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized use</li>
                </ul>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  You must be at least 16 years old to use this Service.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">4. Pricing and Payment</h2>
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">4.1 Pricing Plans</h3>
                <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                  <li><strong>Free Plan:</strong> 3 free caption generations, no payment required</li>
                  <li><strong>Pro Plan:</strong> $4.99/month for unlimited captions and saved favorites</li>
                  <li><strong>Credits Plan:</strong> $2.99 one-time for 10 captions that never expire</li>
                </ul>
                
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mt-3 sm:mt-4">4.2 Payment Terms</h3>
                <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                  <li>All prices are in USD unless otherwise stated</li>
                  <li>Subscription fees are billed monthly in advance</li>
                  <li>Payment is processed through secure third-party payment processors</li>
                  <li>You authorize us to charge your payment method for all fees</li>
                  <li>Credits purchased never expire and are non-refundable</li>
                </ul>

                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mt-3 sm:mt-4">4.3 Cancellation and Refunds</h3>
                <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                  <li>You may cancel your subscription at any time</li>
                  <li>Cancellation takes effect at the end of the current billing period</li>
                  <li>No refunds for partial months or unused credits</li>
                  <li>Refunds may be issued at our sole discretion for technical issues</li>
                </ul>

                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mt-3 sm:mt-4">4.4 Price Changes</h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  We reserve the right to modify pricing with 30 days' notice. Early waitlist members who receive lifetime discounts will maintain their discounted rate.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">5. Acceptable Use</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-2">You agree NOT to:</p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                <li>Upload illegal, harmful, or offensive content</li>
                <li>Violate any intellectual property rights</li>
                <li>Upload images containing minors without proper consent</li>
                <li>Use the Service for spam or unauthorized advertising</li>
                <li>Attempt to reverse engineer or hack the Service</li>
                <li>Share your account credentials with others</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Resell or redistribute generated captions commercially without a business license</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">6. Intellectual Property</h2>
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">6.1 Your Content</h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  You retain all rights to photos you upload. By uploading content, you grant us a limited license to process, store, and display your content solely to provide the Service. We do not claim ownership of your content.
                </p>
                
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mt-3 sm:mt-4">6.2 Generated Captions</h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  Captions generated by our AI are provided for your personal or commercial use. You may use them freely on your social media accounts. However, you may not resell caption generation services using our platform without a commercial agreement.
                </p>

                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mt-3 sm:mt-4">6.3 Our Property</h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  The Service, including its design, code, algorithms, and branding, is owned by TryAIKit and protected by copyright, trademark, and other laws. You may not copy, modify, or create derivative works without permission.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">7. AI-Generated Content Disclaimer</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Captions are generated by artificial intelligence and may not always be accurate, appropriate, or suitable for your needs. We do not guarantee the quality, accuracy, or appropriateness of generated content. You are responsible for reviewing and editing captions before publishing. We are not liable for any consequences resulting from your use of AI-generated captions.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">8. Service Availability</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                We strive to provide reliable service but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or technical issues. We reserve the right to modify, suspend, or discontinue any part of the Service at any time.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">9. Limitation of Liability</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, CAPTION KING AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4 mt-2 sm:mt-3">
                <li>Your use or inability to use the Service</li>
                <li>Any unauthorized access to or use of our servers</li>
                <li>Any bugs, viruses, or malicious code transmitted through the Service</li>
                <li>Any errors or omissions in content or generated captions</li>
                <li>Any conduct or content of third parties</li>
              </ul>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mt-2 sm:mt-3">
                Our total liability shall not exceed the amount you paid us in the past 12 months, or $100, whichever is greater.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">10. Indemnification</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                You agree to indemnify and hold harmless Caption King, its affiliates, and their respective officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the Service, violation of these Terms, or infringement of any rights of another party.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">11. Termination</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the Service will immediately cease. All provisions that should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">12. Governing Law</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which TryAIKit operates, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of that jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">13. Changes to Terms</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or through a notice on the Service. Your continued use after changes constitutes acceptance of the modified Terms. If you disagree with changes, you must stop using the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">14. Severability</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">15. Contact Information</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                For questions about these Terms, please contact us:
              </p>
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-purple-50 rounded-lg">
                <p className="text-sm sm:text-base text-gray-800"><strong>Email:</strong> <a href="mailto:hello@tryaikit.com" className="text-purple-600 hover:text-purple-700">hello@tryaikit.com</a></p>
              </div>
            </section>

            <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-l-4 border-purple-600">
              <p className="text-sm sm:text-base text-gray-800 font-semibold mb-2">
                By using Caption King, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>

          <div className="mt-8 sm:mt-12 text-center">
            <a 
              href="/"
              className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold text-base sm:text-lg"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-8 sm:py-12 mt-12 sm:mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6">
              <div className="flex items-center gap-2">
                <img src="https://public.readdy.ai/ai/img_res/15ec66b2-609e-40f0-bdad-7a5a43050de3.png" alt="Caption King" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
                <span className="text-lg sm:text-2xl font-bold">Caption King</span>
              </div>
              
              <div className="flex flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm">
                <a href="/privacy" className="hover:text-purple-400 transition-colors">Privacy Policy</a>
                <a href="/terms" className="hover:text-purple-400 transition-colors">Terms of Service</a>
                <a href="mailto:hello@tryaikit.com" className="hover:text-purple-400 transition-colors">hello@tryaikit.com</a>
              </div>
            </div>
            
            <div className="text-center text-gray-400 text-xs sm:text-sm mt-6 sm:mt-8">
              © 2025 TryAIKit. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
